import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { size } from 'styled-theme'
import { Box, Flex } from '@rebass/grid'
import { IoClose } from 'react-icons/io5'
import Text from '../../atoms/Text'
import Input from '../../atoms/Input'
import Image from '../../atoms/Image'
import Button from '../../atoms/Button'
import AdSense from '../../atoms/AdSense'
import Viewport from '../../atoms/Viewport'
import ContentBox from '../../atoms/ContentBox'
import AdSenseGroupView from '../../molecules/AdSenseGroupView'
import { handleErrorResponse } from '../../../utlils/general'
import { shareFile } from '../../../utlils/file'
import { useLoading } from '../../../contexts/loading'
import { useAuth } from '../../../contexts/auth'
import api from '../../../api'

const sendPrompt = (body) => {
  return api.post('/prompts/send', body, 'bearer')
}

const sendPromptWithFormData = (formData) => {
  return api.postFormData('/prompts/send', formData, 'bearer')
}

const StyledViewport = styled(Viewport)`
  padding: 4.5rem 3rem 3rem;
  align-items: flex-start;
  @media only screen and (max-width: ${size('mobile')}) {
    padding: 4rem 0 0;
  }
`

const PlaygroundPage = () => {
  const [textPrompt, setTextPrompt] = useState('')
  const [imgPrompt, setImgPrompt] = useState('')
  const [reply, setReply] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [appendImages, setAppendImages] = useState([])
  const { auth, setAuth } = useAuth()
  const { setLoading } = useLoading()

  const onPickImage = useCallback((e) => {
    const files = e.target.files || []
    setAppendImages((prev) => [...prev, ...files])
  }, [])

  const sendPromptToApi = useCallback((prompt, images, type) => {
    setLoading(true)
    return Promise.resolve()
      .then(() => {
        if (images.length > 0) {
          const formData = new FormData()
          images.forEach((file) => {
            formData.append('images', file)
          })
          formData.append('content', prompt)
          return sendPromptWithFormData(formData)
        }
        return sendPrompt({ content: prompt, type })
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
        return { data: null }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [setLoading, setAuth])

  const sendTextPromptToApi = useCallback(() => {
    return sendPromptToApi(textPrompt, appendImages, 'text')
      .then(({ data }) => {
        if (data) setReply(data.result)
      })
  }, [textPrompt, appendImages, sendPromptToApi])

  const sendImagePromptToApi = useCallback(async () => {
    return sendPromptToApi(imgPrompt, [], 'image')
      .then(({ data }) => {
        if (data) setImageUrl(data.result)
      })
  }, [imgPrompt, sendPromptToApi])

  const shareImage = useCallback(async () => {
    try {
      setLoading(true)
      await shareFile(imageUrl, 'image.jpg')
    } catch (err) {
      alert(err.stack || err.message)
    } finally {
      setLoading(false)
    }
  }, [imageUrl, setLoading])

  if (auth.user?.role > 0) {
    return null
  }

  return (
    <StyledViewport>
      <ContentBox>
        <Flex justifyContent="center" mb="2rem">
          <Text palette="primary" bold variant="h1">Playground</Text>
        </Flex>
        <Input
          type="textarea"
          value={textPrompt}
          onChange={(e) => setTextPrompt(e.target.value)}
          rows={6}
        />
        {appendImages.length > 0 && (
          <Flex flexWrap="wrap" alignItems="center" mt="0.5rem" style={{ gap: '0.5rem' }}>
            {appendImages.map((img, iIdx) => (
              <Flex
                key={`append-image-${iIdx}`}
                alignItems="center"
                justifyContent="center"
                style={{ position: 'relative' }}
              >
                <Image
                  src={URL.createObjectURL(img)}
                  width={100}
                  height={100}
                  borderRadius="0.5rem"
                  style={{ objectFit: 'contain' }}
                />
                <Button
                  palette="error"
                  padding={0}
                  onClick={() => setAppendImages((prev) => prev.filter((_, i) => i !== iIdx))}
                  style={{ position: 'absolute', top: 2, right: 2, borderRadius: '50%' }}
                >
                  <IoClose size={20} />
                </Button>
              </Flex>
            ))}
          </Flex>
        )}
        <Flex mt="0.5rem">
          <Input type="file" onChange={onPickImage} multiple />
        </Flex>
        <Button
          fullWidth
          onClick={sendTextPromptToApi}
          style={{ marginTop: '0.5rem' }}
        >
          Get Reply
        </Button>
        {!!reply && (
          <Box mt="0.5rem">
            <Text paletteIndex={2} style={{ marginBottom: '0.25rem' }}>Reply:</Text>
            <br />
            <Text>{reply}</Text>
          </Box>
        )}
        <Box mt="3rem">
          <Input
            type="textarea"
            value={imgPrompt}
            onChange={(e) => setImgPrompt(e.target.value)}
            rows={4}
          />
          <Button
            fullWidth
            onClick={sendImagePromptToApi}
            style={{ marginTop: '0.5rem' }}
          >
            Generate Image
          </Button>
        </Box>
        {!!imageUrl && (
          <Flex mt="0.5rem" flexDirection="column">
            <Text>Result:</Text>
            <Image
              key={imageUrl}
              src={imageUrl}
              width="100%"
              height="100%"
              borderRadius="0.5rem"
              style={{ maxWidth: 400 }}
            />
            <Button onClick={shareImage} style={{ marginTop: '0.5rem' }}>
              Share
            </Button>
          </Flex>
        )}
        <AdSense
          adSlotId="9477289676"
          data-full-width-responsive="false"
          style={{ height: 120, width: '100%' }}
          containerProps={{ width: 1, height: 120, mt: '2rem' }}
        />
      </ContentBox>
      <AdSenseGroupView />
    </StyledViewport>
  )
}

export default PlaygroundPage
